'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useRef } from 'react';
import { fadeIn } from '@/lib/animations';
import { 
  Settings, 
  ArrowUpRight, 
  ShieldCheck, 
  MapPin,
  Clock,
  Warehouse,
  Truck
} from 'lucide-react';

const benefits = [
  {
    title: 'Customized Solutions',
    description: 'We understand that every business is unique. Our services are tailored to meet your specific requirements.',
    icon: Settings,
  },
  {
    title: 'Scalability',
    description: 'Whether you\'re a small business or a large enterprise, our solutions scale with your growth.',
    icon: ArrowUpRight,
  },
  {
    title: 'Compliance and Safety',
    description: 'Our operations adhere to industry standards, ensuring your products are handled with the utmost care and compliance.',
    icon: ShieldCheck,
  },
  {
    title: 'Strategic Location',
    description: 'Access to key markets through our strategically positioned Wisconsin facilities.',
    icon: MapPin,
  },
  {
    title: 'Time Savings',
    description: 'Focus on your core business while we handle the complexities of logistics management.',
    icon: Clock,
  },
];

export function Benefits() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const truckY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section id="benefits" className="py-32 pb-48 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">Why Choose Us</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            The Lindner Logistics Advantage
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            Partner with us to experience these key benefits that have made us Wisconsin's leading 3PL provider.
          </p>
        </div>
        
        <div ref={containerRef} className="relative max-w-7xl mx-auto min-h-[800px]">
          {/* Vertical Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 bg-gradient-to-b from-brand-200 via-brand-400 to-brand-600" />

          {/* Top Warehouse */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600">
              <Warehouse size={32} />
            </div>
          </div>

          {/* Animated Truck */}
          <motion.div
            style={{ top: truckY }}
            className="absolute left-1/2 -translate-x-1/2 z-20"
          >
            <div className="w-12 h-12 bg-brand-600 rounded-full flex items-center justify-center text-white transform -translate-y-1/2">
              <Truck size={24} />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-brand-600 rounded-full animate-ping opacity-20" />
          </motion.div>

          {/* Bottom Warehouse */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600">
              <Warehouse size={32} />
            </div>
          </div>

          {/* Benefits */}
          <div className="relative z-10">
            {benefits.map((benefit, index) => {
              const [ref, inView] = useInView({
                triggerOnce: true,
                threshold: 0.05,
                rootMargin: '200px 0px',
              });

              const isLeft = index % 2 === 0;
              const topOffset = `${(index * 160) + 60}px`;

              return (
                <motion.div
                  key={index}
                  ref={ref}
                  initial="hidden"
                  animate={inView ? "show" : "hidden"}
                  variants={fadeIn(isLeft ? 'left' : 'right')}
                  className={`absolute ${isLeft ? 'left-0 pr-8' : 'right-0 pl-8'} w-[calc(50%-32px)]`}
                  style={{ top: topOffset }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
                      <benefit.icon size={24} />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                      <p className="text-gray-700">{benefit.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}